import { NSARouterMakerResult } from "namirasoft-account-react";
import { BaseMetaColumn, BaseMetaTable, BaseServerConfig, FilterItem, FilterItemOperator, IStorageLocal, SortItem } from "namirasoft-core";
import { AccountRow, BalanceSheetRow, BalanceSheetRowRow, CurrencyRow, NamirasoftERPMetaDatabase, PartyRow, PartyTypeRow, ReportType, TagRow } from "namirasoft-erp";
import { NamirasoftMap } from "namirasoft-site-map";
import { BaseColumnFormatter, DateTimeFormatter, FloatFormatter, IDFormatter, IntegerFormatter, NSBox, NSBoxBoolean, NSBoxDate, NSBoxEntity, NSBoxEnum, NSButtonBlue, NSColumn, NSLine, NSLoading, NSPanel, NSRow, NSSpace, NSSpaceSizeType, NSTable, StringFormatter, StringFormatterSizeType, TableColumnInfo, TableRowInfo } from "namirasoft-site-react";
import { Component, createRef } from "react";
import { ErpLayout } from "../ErpLayout";
import { ErpRouterProps } from "../ErpRouterProps";
import { ErpScope, ErpScopeField, ErpScopeMode } from "../ErpScope";

interface BalanceSheetReportPageProps extends NSARouterMakerResult, ErpRouterProps { }

interface TableRow
{
    line_id: string;
    account_id: string | null;
    account_code: string | null;
    parent_account_id: string | null;
    party_type_id: string | null;
    party_id: string | null;
    count: number;
    min_date_time: Date | string | null;
    max_date_time: Date | string | null;
    debit: number | string;
    credit: number | string;
    balance_debit: string;
    balance_credit: string;
    currency_id: string | null;
}

interface BalanceSheetReportPageState
{
    loading: boolean;
    report: BalanceSheetRow | null;
    currencySymbol: string;
    currencySymbols: { [id: string]: string };
    scopeVersion: number;
}

type ScopedTable = BaseMetaTable & {
    columns: {
        company_id?: BaseMetaColumn;
        branch_id?: BaseMetaColumn;
        enabled: BaseMetaColumn;
    };
};

type ListFn<TRow> = (
    filters: FilterItem[] | null,
    page: number | null,
    size: number | null,
    sorts: SortItem[],
    all_workspaces: boolean | null,
    config?: BaseServerConfig<any>,
) => Promise<{ rows: TRow[]; count: number }>;

interface BalanceSplitInput
{
    balance_debit?: number | string | null;
    balance_credit?: number | string | null;
    balance?: number | string | null;
    debit?: number | string | null;
    credit?: number | string | null;
}

interface CurrencyGroup
{
    debit: number;
    credit: number;
    count: number;
    min: Date | string | null;
    max: Date | string | null;
}

export default class BalanceSheetReportPage extends Component<BalanceSheetReportPageProps, BalanceSheetReportPageState>
{
    private static readonly TOTAL_ROW_ID = "__total__";
    private static readonly TOTAL_ROW_PREFIX = "__total__::";

    override state: BalanceSheetReportPageState = {
        loading: false,
        report: null,
        currencySymbol: "",
        currencySymbols: {},
        scopeVersion: 0,
    };

    private unsubscribeScope: (() => void) | null = null;
    private lastCompanyId: string | null = null;
    private lastBranchId: string | null = null;

    FromDate = createRef<NSBoxDate>();
    AsOf = createRef<NSBoxDate>();
    Accounts = createRef<NSBoxEntity<AccountRow>>();
    PartyType = createRef<NSBoxEntity<PartyTypeRow>>();
    Party = createRef<NSBoxEntity<PartyRow>>();
    Currency = createRef<NSBoxEntity<CurrencyRow>>();
    Tags = createRef<NSBoxEntity<TagRow>>();
    ByAccount = createRef<NSBoxBoolean>();
    ByParty = createRef<NSBoxBoolean>();
    HasParty = createRef<NSBoxBoolean>();
    HasTag = createRef<NSBoxBoolean>();
    ShowZero = createRef<NSBoxBoolean>();
    ShowDebit = createRef<NSBoxBoolean>();
    ShowCredit = createRef<NSBoxBoolean>();
    ConvertToBranchCurrency = createRef<NSBoxBoolean>();
    ReportTypeRef = createRef<NSBoxEnum<ReportType>>();
    IncludeRequests = createRef<NSBoxBoolean>();

    override componentDidMount(): void
    {
        this.lastCompanyId = ErpScope.getCompanyIdRaw();
        this.lastBranchId = ErpScope.getBranchIdRaw();
        this.unsubscribeScope = ErpScope.subscribe(() =>
        {
            let companyId = ErpScope.getCompanyIdRaw();
            let branchId = ErpScope.getBranchIdRaw();
            let scopeChanged = companyId !== this.lastCompanyId || branchId !== this.lastBranchId;
            this.lastCompanyId = companyId;
            this.lastBranchId = branchId;

            this.setState(s => ({ scopeVersion: s.scopeVersion + 1, report: null }));
            if (!scopeChanged)
                return;

            this.Accounts.current?.setValueAll([]);
            this.PartyType.current?.setValueAll([]);
            this.Party.current?.setValueAll([]);
            this.Currency.current?.setValueOne(null);
            this.Tags.current?.setValueAll([]);
        });
    }

    override componentWillUnmount(): void { this.unsubscribeScope?.(); }

    private onPartyTypeChanged = (): void =>
    {
        this.Party.current?.setValueAll([]);
        this.Party.current?.reload();
    };

    private refreshCurrencySymbol(): void
    {
        let currencyId = this.Currency.current?.getValueOne(false) ?? null;
        if (!currencyId)
        {
            this.setState({ currencySymbol: "" });
            return;
        }
        this.props.servers.erp.currency.Get(currencyId)
            .then(c => this.setState({ currencySymbol: c?.symbol ?? "" }))
            .catch(() => this.setState({ currencySymbol: "" }));
    }

    private runReport = (onFinished: () => void): void =>
    {
        let companyId: string | null;
        let branchId: string | null;
        let fiscalYearId: string | null;
        try
        {
            companyId = ErpScope.getCompanyIdOrNull();
            branchId = ErpScope.getBranchIdOrNull();
            fiscalYearId = ErpScope.getFiscalYearIdOrNull();
        }
        catch (err)
        {
            this.props.notifier.onError(err as Error);
            onFinished();
            return;
        }

        let fromRaw = this.FromDate.current?.getValue(false) ?? null;
        let asOfRaw = this.AsOf.current?.getValue() ?? null;

        if (!asOfRaw)
        {
            onFinished();
            return;
        }

        let accountIds = this.Accounts.current?.getValueAll() ?? [];
        let partyTypeIds = this.PartyType.current?.getValueAll() ?? [];
        let partyIds = this.Party.current?.getValueAll() ?? [];
        let tagIds = this.Tags.current?.getValueAll() ?? [];
        let currencyId = this.Currency.current?.getValueOne(false) ?? null;
        let hasParty = this.HasParty.current?.getValueNull(false) ?? null;
        let hasTag = this.HasTag.current?.getValueNull(false) ?? null;
        let groupByAccount = this.ByAccount.current?.getValueNull(false) ?? true;
        let groupByParty = this.ByParty.current?.getValueNull(false) ?? false;
        let showZero = this.ShowZero.current?.getValueNull(false) ?? false;
        let showDebit = this.ShowDebit.current?.getValueNull(false) ?? true;
        let showCredit = this.ShowCredit.current?.getValueNull(false) ?? true;
        let convertToBranchCurrency = this.ConvertToBranchCurrency.current?.getValueNull(false) ?? false;
        let reportType = this.ReportTypeRef.current?.getValueEnumOne(ReportType, ReportType.TrialBalance) ?? ReportType.TrialBalance;
        let includeRequests = this.IncludeRequests.current?.getValueNull(false) ?? false;

        let orCsv = (arr: string[]): string | null => arr.length > 0 ? arr.join(",") : null;

        this.refreshCurrencySymbol();
        this.setState({ loading: true });

        this.props.servers.erp.report.BalanceSheet(
            companyId,
            branchId,
            fiscalYearId,
            fromRaw ? new Date(fromRaw) : null,
            new Date(asOfRaw),
            orCsv(accountIds),
            orCsv(partyTypeIds),
            orCsv(partyIds),
            orCsv(tagIds),
            currencyId,
            hasParty,
            hasTag,
            groupByAccount,
            groupByParty,
            showZero,
            showDebit,
            showCredit,
            convertToBranchCurrency,
            reportType,
            includeRequests,
        ).then((res: BalanceSheetRow) =>
        {
            this.setState({ loading: false, report: res });
            this.loadCurrencySymbols(res);
            onFinished();
        }).catch(() =>
        {
            // The server already reported the error through the notifier.
            this.setState({ loading: false });
            onFinished();
        });
    };

    private formatMoney(value: string | number, currencyId?: string | null): string
    {
        let n = Number(value) || 0;
        let formatted = n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        let symbol = "";
        if (currencyId && this.state.currencySymbols[currencyId])
            symbol = this.state.currencySymbols[currencyId];
        else
            symbol = this.state.currencySymbol?.trim() ?? "";
        return symbol ? `${symbol} ${formatted}` : formatted;
    }

    private loadCurrencySymbols(report: BalanceSheetRow | null): void
    {
        if (!report) return;
        let ids = new Set<string>();
        for (let r of report.rows)
            if (r.currency_id) ids.add(r.currency_id);
        let missing = Array.from(ids).filter(id => !this.state.currencySymbols[id]);
        if (missing.length === 0) return;
        Promise.all(missing.map(id => this.props.servers.erp.currency.Get(id).catch(() => null)))
            .then(list =>
            {
                let map = { ...this.state.currencySymbols };
                list.forEach((c, i) => { if (c) map[missing[i]] = c.symbol ?? ""; });
                this.setState({ currencySymbols: map });
            });
    }

    private buildColumns(): TableColumnInfo[]
    {
        let db = new NamirasoftERPMetaDatabase(this.props.account.token_manager, console.error);
        let namirasoft = new NamirasoftMap(this.props.account.token_manager, console.error);
        let line = db.tables.journal_entry_line;
        let je = db.tables.journal_entry;
        let countCol = new BaseMetaColumn(line, "count", "Count", line.columns.debit.type, true);
        let minDateCol = new BaseMetaColumn(line, "min_date_time", "Min Date", je.columns.date_time.type, true);
        let maxDateCol = new BaseMetaColumn(line, "max_date_time", "Max Date", je.columns.date_time.type, true);
        let balanceDebitCol = new BaseMetaColumn(line, "balance_debit", "Balance Debit", line.columns.debit.type, true);
        let balanceCreditCol = new BaseMetaColumn(line, "balance_credit", "Balance Credit", line.columns.credit.type, true);
        let currencyCol = new BaseMetaColumn(line, "currency_id", "Currency", db.tables.currency.columns.id.type, true);
        let cols: TableColumnInfo[] = [];
        let accountCodeCol = new BaseMetaColumn(line, "account_code", "Code", db.tables.account.columns.code.type, true);
        cols.push(BaseColumnFormatter.getColumn(accountCodeCol, new StringFormatter(StringFormatterSizeType.TwoWords)));
        cols.push(BaseColumnFormatter.getColumn(line.columns.account_id, new IDFormatter(namirasoft, new IStorageLocal())));
        cols.push(BaseColumnFormatter.getColumn(line.columns.party_id, new IDFormatter(namirasoft, new IStorageLocal())));
        cols.push(BaseColumnFormatter.getColumn(line.columns.debit, new FloatFormatter("150px")));
        cols.push(BaseColumnFormatter.getColumn(line.columns.credit, new FloatFormatter("150px")));
        cols.push(BaseColumnFormatter.getColumn(balanceDebitCol, new FloatFormatter("150px")));
        cols.push(BaseColumnFormatter.getColumn(balanceCreditCol, new FloatFormatter("150px")));
        cols.push(BaseColumnFormatter.getColumn(currencyCol, new IDFormatter(namirasoft, new IStorageLocal(), "120px")));
        cols.push(BaseColumnFormatter.getColumn(countCol, new IntegerFormatter("75px")));
        cols.push(BaseColumnFormatter.getColumn(minDateCol, new DateTimeFormatter()));
        cols.push(BaseColumnFormatter.getColumn(maxDateCol, new DateTimeFormatter()));
        return cols;
    }

    private getTotalLabelColumnName(): string
    {
        return "account_id";
    }

    private getRows = async (): Promise<{ rows: TableRow[]; count: number }> =>
    {
        let { report } = this.state;
        if (!report) return { rows: [], count: 0 };

        let splitBalance = (src: BalanceSplitInput): { balance_debit: string; balance_credit: string } =>
        {
            if (src.balance_debit !== undefined || src.balance_credit !== undefined)
                return {
                    balance_debit: String(src.balance_debit ?? "0"),
                    balance_credit: String(src.balance_credit ?? "0"),
                };
            let n = Number(src.balance ?? Number(src.debit ?? 0) - Number(src.credit ?? 0));
            return {
                balance_debit: n > 0 ? n.toFixed(4) : "0.0000",
                balance_credit: n < 0 ? (-n).toFixed(4) : "0.0000",
            };
        };

        let dataRows: TableRow[] = report.rows.map((r: BalanceSheetRowRow) =>
        {
            let split = splitBalance(r);
            return {
                line_id: `${r.account_id ?? ""}::${r.party_type_id ?? ""}::${r.party_id ?? ""}::${r.currency_id ?? ""}`,
                account_id: r.account_id,
                account_code: r.account_code ?? null,
                parent_account_id: r.parent_account_id ?? null,
                party_type_id: r.party_type_id,
                party_id: r.party_id,
                count: r.count ?? 0,
                min_date_time: r.min_date_time ?? null,
                max_date_time: r.max_date_time ?? null,
                debit: r.debit,
                credit: r.credit,
                balance_debit: split.balance_debit,
                balance_credit: split.balance_credit,
                currency_id: r.currency_id ?? null,
            };
        });

        let groups = new Map<string, CurrencyGroup>();
        for (let r of report.rows)
        {
            if (r.parent_account_id)
                continue;
            let key = r.currency_id ?? "";
            let g = groups.get(key);
            if (!g) { g = { debit: 0, credit: 0, count: 0, min: null, max: null }; groups.set(key, g); }
            g.debit += Number(r.debit ?? 0);
            g.credit += Number(r.credit ?? 0);
            g.count += Number(r.count ?? 0);
            if (r.min_date_time && (!g.min || r.min_date_time < g.min)) g.min = r.min_date_time;
            if (r.max_date_time && (!g.max || r.max_date_time > g.max)) g.max = r.max_date_time;
        }

        let totalRows: TableRow[] = [];
        for (let [currencyKey, g] of groups)
        {
            let split = splitBalance({ debit: g.debit, credit: g.credit });
            totalRows.push({
                line_id: BalanceSheetReportPage.TOTAL_ROW_PREFIX + currencyKey,
                account_id: BalanceSheetReportPage.TOTAL_ROW_PREFIX + currencyKey,
                account_code: null,
                parent_account_id: "__total_parent__",
                party_type_id: null,
                party_id: null,
                count: g.count,
                min_date_time: g.min,
                max_date_time: g.max,
                debit: String(g.debit),
                credit: String(g.credit),
                balance_debit: split.balance_debit,
                balance_credit: split.balance_credit,
                currency_id: currencyKey || null,
            });
        }

        let rows: TableRow[] = [...dataRows, ...totalRows];
        return { rows, count: rows.length };
    };

    private getCellFormattedValue = (value: any, column: TableColumnInfo, row: TableRowInfo<TableRow>, printable: boolean): any =>
    {
        let lineId: string = row.value.line_id;
        let isTotal = lineId === BalanceSheetReportPage.TOTAL_ROW_ID || lineId.startsWith(BalanceSheetReportPage.TOTAL_ROW_PREFIX);

        let rowCurrencyId: string | null = row.value.currency_id ?? null;

        if (isTotal)
        {
            if (column.name === this.getTotalLabelColumnName()) return "Total";
            if (column.name === "debit" || column.name === "credit" || column.name === "balance_debit" || column.name === "balance_credit")
                return this.formatMoney(value, rowCurrencyId);
            if (column.name === "count" || column.name === "min_date_time" || column.name === "max_date_time" || column.name === "currency_id")
                return column.formatter.format(value, column, row, printable);
            return "";
        }

        if (column.name === "debit" || column.name === "credit" || column.name === "balance_debit" || column.name === "balance_credit")
        {
            let formatted = this.formatMoney(value, rowCurrencyId);
            let isZero = !value || Number(value) === 0;
            return isZero ? <span style={{ color: "#bdbdbd" }}>{formatted}</span> : formatted;
        }
        if ((column.name === "account_id" || column.name === "account_code" || column.name === "party_type_id" || column.name === "party_id")
            && (value === null || value === undefined || value === ""))
            return <span style={{ color: "#bdbdbd" }}>-</span>;

        return column.formatter.format(value, column, row, printable);
    };

    private getRowAttributes = (row_value: TableRow, _row_index: number): { [key: string]: any } =>
    {
        let lineId: string = row_value.line_id;
        let isTotal = lineId === BalanceSheetReportPage.TOTAL_ROW_ID || lineId.startsWith(BalanceSheetReportPage.TOTAL_ROW_PREFIX);
        if (!isTotal) return {};
        return {
            style: {
                fontWeight: "bold",
                color: "hsla(234, 64%, 22%, 1)",
                background: "#fafbff",
                borderTop: "2px solid #141B5C",
            },
        };
    };

    private renderTable(): React.ReactNode
    {
        let { report } = this.state;
        if (!report)
            return null;

        return (
            <div style={{ width: "100%", maxWidth: "100%" }}>
                <NSLine />
                <NSSpace size={NSSpaceSizeType.MINI} />
                <NSTable<TableRow>
                    key={String(this.state.scopeVersion) + ":" + String(report.rows.length)}
                    name="balance-sheet"
                    checkbox={false}
                    columns={this.buildColumns()}
                    getRows={this.getRows}
                    getRowKey={(row) => row.value.line_id}
                    getCellFormattedValue={this.getCellFormattedValue}
                    getRowAttributes={this.getRowAttributes}
                    sort={{ enabled: false }}
                    hierarchy={{
                        id: "account_id",
                        parent: "parent_account_id",
                        group: ["currency_id"],
                    }}
                    ui={{ pagination: false, grow: true }}
                />
            </div>
        );
    }

    override render(): React.ReactNode
    {
        let { servers } = this.props;
        let db = new NamirasoftERPMetaDatabase(this.props.account.token_manager, console.error);

        let scopedList = <TRow,>(table: ScopedTable, list: ListFn<TRow>): (filters: FilterItem[] | null, page: number | null, size: number | null, sorts: SortItem[]) => Promise<{ rows: TRow[]; count: number }> =>
        {
            return (filters, page, size, sorts) =>
            {
                let companyId = ErpScope.getCompanyIdOrNull();
                let branchId = ErpScope.getBranchIdOrNull();
                let f: FilterItem[] = filters ? [...filters] : [];
                if (companyId && table.columns.company_id)
                    f.push(new FilterItem(table, table.columns.company_id, false, FilterItemOperator.all.equals, companyId));
                if (branchId && table.columns.branch_id)
                    f.push(new FilterItem(table, table.columns.branch_id, false, FilterItemOperator.all.equals, branchId));
                f.push(new FilterItem(table, table.columns.enabled, false, FilterItemOperator.all.equals, "1"));
                return list(f, page, size, sorts, null);
            };
        };

        return (
            <ErpLayout
                {...this.props}
                mode={ErpScopeMode.Filter}
                fields={[ErpScopeField.Company, ErpScopeField.Branch, ErpScopeField.FiscalYear]}
                action={{ title: "Balance Sheet" }}
            >
                <NSColumn style={{ width: "100%", alignItems: "center" }}>
                    <NSRow style={{ flexWrap: "wrap", alignItems: "center" }}>
                        <NSBoxDate
                            ref={this.FromDate}
                            title="From Date"
                            required={false}
                        />
                        <NSBoxDate
                            ref={this.AsOf} required
                            title="As Of" info={{ link: "https://namirasoft.com/erp/console-guide/balance-sheet/#field-as-of-date" }}
                            defaultValue={new Date()}
                        />
                        <NSBoxEnum<ReportType>
                            ref={this.ReportTypeRef}
                            title="Report Type"
                            required
                            multiple={false}
                            getEnumObject={() => ReportType}
                            defaultValue={ReportType.TrialBalance}
                        />
                        <NSBoxEntity<AccountRow>
                            ref={this.Accounts}
                            title="Accounts" info={{ link: "https://namirasoft.com/erp/console-guide/balance-sheet/#field-account-ids" }}
                            required={false}
                            multiple={true}
                            table_name="account"
                            table={db.tables.account}
                            list={scopedList<AccountRow>(db.tables.account, servers.erp.account.List)}
                            getValue={(item) => item.id} getTitle={(item) => `${item.code} - ${item.name}`} getSort={() => null}
                            placeholder="Accounts"
                            style={{ width: NSBox.width.double }}
                        />
                        <NSBoxEntity<PartyTypeRow>
                            ref={this.PartyType}
                            title="Party Type" info={{ link: "https://namirasoft.com/erp/console-guide/balance-sheet/#field-party-type-ids" }}
                            required={false}
                            multiple={true}
                            table_name="party_type"
                            table={db.tables.party_type}
                            list={scopedList<PartyTypeRow>(db.tables.party_type, servers.erp.party_type.List)}
                            getValue={(item) => item.id} getTitle={(item) => item.name} getSort={() => null}
                            placeholder="Party Type"
                            onChanged={this.onPartyTypeChanged}
                        />
                        <NSBoxEntity<PartyRow>
                            ref={this.Party}
                            title="Party" info={{ link: "https://namirasoft.com/erp/console-guide/balance-sheet/#field-party-ids" }}
                            required={false}
                            multiple={true}
                            table_name="party"
                            table={db.tables.party}
                            list={async (filters, page, size, sorts) =>
                            {
                                let ptIds = this.PartyType.current?.getValueAll() ?? [];
                                let scoped = scopedList<PartyRow>(db.tables.party, servers.erp.party.List);
                                let result = await scoped(filters, page, size, sorts);
                                if (ptIds.length > 0)
                                {
                                    let allowedSet = new Set(ptIds);
                                    let rows = result.rows.filter(r => allowedSet.has(r.party_type_id));
                                    return { count: rows.length, rows };
                                }
                                return result;
                            }}
                            getValue={(item) => item.id} getTitle={(item) => item.name} getSort={() => null}
                            placeholder="Party"
                        />
                        <NSBoxEntity<TagRow>
                            ref={this.Tags}
                            title="Tags" info={{ link: "https://namirasoft.com/erp/console-guide/balance-sheet/#field-tag-ids" }}
                            required={false} multiple={true}
                            table_name="tag"
                            table={db.tables.tag}
                            list={scopedList<TagRow>(db.tables.tag, servers.erp.tag.List)}
                            getValue={(item) => item.id} getTitle={(item) => item.name} getSort={() => null}
                            placeholder="Tags"
                        />
                        <NSBoxEntity<CurrencyRow>
                            ref={this.Currency}
                            title="Currency" info={{ link: "https://namirasoft.com/erp/console-guide/balance-sheet/#field-currency-id" }}
                            required={false}
                            multiple={false}
                            table_name="currency"
                            table={db.tables.currency}
                            list={(filters, page, size, sorts) =>
                            {
                                let f: FilterItem[] = filters ? [...filters] : [];
                                f.push(new FilterItem(db.tables.currency, db.tables.currency.columns.enabled, false, FilterItemOperator.all.equals, "1"));
                                return servers.erp.currency.List(f, page, size, sorts, null);
                            }}
                            getValue={(item) => item.id} getTitle={(item) => item.name} getSort={() => null}
                            placeholder="Currency"
                        />
                        <NSPanel title="Group By" style={{ padding: 8, display: "flex", flexDirection: "column", gap: 4 }}>
                            <NSRow style={{ gap: 8, flexWrap: "wrap", flexDirection: "row" }}>
                                <NSBoxBoolean
                                    ref={this.ByAccount}
                                    required={false}
                                    checked={true}
                                    title="By Account" info={{ link: "https://namirasoft.com/erp/console-guide/balance-sheet/#field-group-by-account" }}
                                    hideHeader
                                    style={{ width: 150, flexGrow: 0 }}
                                />
                                <NSBoxBoolean
                                    ref={this.ByParty}
                                    required={false}
                                    checked={false}
                                    title="By Party" info={{ link: "https://namirasoft.com/erp/console-guide/balance-sheet/#field-group-by-party" }}
                                    hideHeader
                                    style={{ width: 150, flexGrow: 0 }}
                                />
                            </NSRow>
                        </NSPanel>
                        <NSPanel title="Filter" style={{ padding: 8, display: "flex", flexDirection: "column", gap: 4 }}>
                            <NSRow style={{ gap: 8, flexWrap: "wrap", flexDirection: "row" }}>
                                <NSBoxBoolean
                                    ref={this.HasParty}
                                    required={false}
                                    triState
                                    checked={null}
                                    title="Has Party" info={{ link: "https://namirasoft.com/erp/console-guide/balance-sheet/#field-has-party" }}
                                    hideHeader
                                    style={{ width: 150, flexGrow: 0 }}
                                />
                                <NSBoxBoolean
                                    ref={this.HasTag}
                                    required={false}
                                    triState
                                    checked={null}
                                    title="Has Tag" info={{ link: "https://namirasoft.com/erp/console-guide/balance-sheet/#field-has-tag" }}
                                    hideHeader
                                    style={{ width: 150, flexGrow: 0 }}
                                />
                                <NSBoxBoolean
                                    ref={this.ShowZero}
                                    required={false}
                                    checked={false}
                                    title="Show Zero" info={{ link: "https://namirasoft.com/erp/console-guide/balance-sheet/#field-show-zero" }}
                                    hideHeader
                                    style={{ width: 150, flexGrow: 0 }}
                                />
                                <NSBoxBoolean
                                    ref={this.ShowDebit}
                                    required={false}
                                    checked={true}
                                    title="Show Debit" info={{ link: "https://namirasoft.com/erp/console-guide/balance-sheet/#field-show-debit" }}
                                    hideHeader
                                    style={{ width: 150, flexGrow: 0 }}
                                />
                                <NSBoxBoolean
                                    ref={this.ShowCredit}
                                    required={false}
                                    checked={true}
                                    title="Show Credit" info={{ link: "https://namirasoft.com/erp/console-guide/balance-sheet/#field-show-credit" }}
                                    hideHeader
                                    style={{ width: 150, flexGrow: 0 }}
                                />
                                <NSBoxBoolean
                                    ref={this.IncludeRequests}
                                    required={false}
                                    checked={false}
                                    title="Include Requests" info={{ link: "https://namirasoft.com/erp/console-guide/balance-sheet/#field-include-requests" }}
                                    hideHeader
                                    style={{ width: 170, flexGrow: 0 }}
                                />
                            </NSRow>
                        </NSPanel>
                        <NSPanel title="Conversion" style={{ padding: 8, display: "flex", flexDirection: "column", gap: 4 }}>
                            <NSRow style={{ gap: 8, flexWrap: "wrap", flexDirection: "row" }}>
                                <NSBoxBoolean
                                    ref={this.ConvertToBranchCurrency}
                                    required={false}
                                    checked={false}
                                    hideHeader
                                    title="Convert To Branch Currency" info={{ link: "https://namirasoft.com/erp/console-guide/balance-sheet/#field-convert-to-branch-currency" }}
                                    style={{ width: 220, flexGrow: 0 }}
                                />
                            </NSRow>
                        </NSPanel>
                        <NSButtonBlue title="Run Report" onClick={{ action: this.runReport, showLoading: true }} />
                    </NSRow>
                    {this.state.loading && <NSLoading />}
                    {!this.state.loading && this.renderTable()}
                </NSColumn>
            </ErpLayout >
        );
    }
}